/* global bootstrap */
import { useEffect } from 'react';
import {
    isAndroid,
    isAndroidTablet,
    isDesktop,
    isInStandaloneMode,
    isIPad,
    isIPhone,
} from '../util/deviceDetection';

const INSTALL_KEY = 'xpenso-install-attempts';
const INSTALL_LAST = 'xpenso-install-last';
const INSTALL_DONE = 'xpenso-install-done';

export default function usePWAInstall() {
    useEffect(() => {
        let offcanvasInstance = null;

        const pathname = window.location.pathname;
        const isHomeRoute = pathname === '/' || pathname === '';

        const getAttempts = () => parseInt(localStorage.getItem(INSTALL_KEY) || '0', 10);

        const setAttempts = (n) => localStorage.setItem(INSTALL_KEY, n.toString());

        const getLastTime = () => parseInt(localStorage.getItem(INSTALL_LAST) || '0', 10);

        const setLastTime = (ts) => localStorage.setItem(INSTALL_LAST, ts.toString());

        const markInstalled = () => localStorage.setItem(INSTALL_DONE, 'true');

        const isInstalled = () => localStorage.getItem(INSTALL_DONE) === 'true';

        const showIOSBottomSheet = () => {
            const el = document.getElementById('ios-install-sheet');
            if (!el) return;

            document.querySelectorAll('.offcanvas-backdrop').forEach((n) => n.remove());

            const instance = new bootstrap.Offcanvas(el);
            instance.show();

            el.addEventListener('hidden.bs.offcanvas', () => {
                // Remove any leftover backdrop
                document.querySelectorAll('.offcanvas-backdrop').forEach((n) => n.remove());

                // Restore scrolling on iOS Safari
                document.body.style.overflow = 'auto';
                document.body.style.position = 'relative';
                document.documentElement.style.overflow = 'auto'; // optional, for extra safety

                void document.body.offsetHeight;
            });
        };

        const showInstallPopup = () => {
            if (isInstalled()) return;

            const now = Date.now();
            const last = getLastTime();
            const attempts = getAttempts();

            if (attempts >= 100 && now - last < 2 * 24 * 60 * 60 * 1000) {
                return;
            }

            const el = document.getElementById('offcanvas');
            if (!el) return;

            // Remove ANY existing backdrops
            document.querySelectorAll('.offcanvas-backdrop').forEach((n) => n.remove());

            offcanvasInstance = new bootstrap.Offcanvas(el);
            offcanvasInstance.show();

            setAttempts(attempts + 1);
            setLastTime(now);

            el.addEventListener('hidden.bs.offcanvas', () => {
                document.querySelectorAll('.offcanvas-backdrop').forEach((n) => n.remove());
            });
        };

        // 1️⃣ If already in standalone mode or installed, don't show prompts
        if (isInStandaloneMode || isInstalled()) return;

        const beforeInstallHandler = (e) => {
            e.preventDefault();

            // Save globally so navigation does not lose it
            window.deferredPromptGlobal = e;

            // Only show popup for mobile (not desktop)
            if (!isDesktop && isHomeRoute) {
                showInstallPopup();
            }
        };

        const clickHandler = async () => {
            const dp = window.deferredPromptGlobal;
            if (!dp) return;

            dp.prompt();

            const { outcome } = await dp.userChoice;

            if (outcome === 'accepted') {
                markInstalled();
            }

            setTimeout(() => {
                const el = document.getElementById('offcanvas');
                if (el) {
                    const inst = bootstrap.Offcanvas.getInstance(el) || new bootstrap.Offcanvas(el);
                    inst.hide();
                }
            }, 50);

            window.deferredPromptGlobal = null;
        };

        // DOM button binding
        const interval = setInterval(() => {
            const btn = document.getElementById('installapp');
            if (btn) {
                btn.onclick = clickHandler;
                clearInterval(interval);
            }
        }, 300);

        if (isIPhone || isIPad) {
            // Only auto-show on Home. Other pages (like Settings) can still open it manually.
            if (isHomeRoute) {
                setTimeout(showIOSBottomSheet, 800);
            }
            return;
        } else if (isAndroid || isAndroidTablet || isDesktop) {
            // Android + Desktop browsers (Chrome/Edge) can fire beforeinstallprompt
            // Keep this listener as a fallback — index.js already has an early global one
            window.addEventListener('beforeinstallprompt', beforeInstallHandler);
        }

        // If the event was fired BEFORE navigation (captured by index.js early listener)
        if (window.deferredPromptGlobal) {
            if (!isDesktop && isHomeRoute) {
                setTimeout(showInstallPopup, 500);
            }
        }

        return () => {
            clearInterval(interval);
            // Do NOT remove beforeinstallprompt listener — we want it to persist globally
        };
    }, []);
}
